// src/components/InstitutionSelect/InstitutionSelect.jsx
import React, { useState, useRef, useEffect } from "react";
import { NIGERIAN_INSTITUTIONS, INSTITUTION_TYPES, getInstitutionsByType } from "../../data/nigerianInstitutions";
import "./InstitutionSelect.css";

const InstitutionSelect = ({ value, onChange, error, placeholder = "Search or select institution..." }) => {
  const [query, setQuery] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState("all"); // "all" | type string
  const containerRef = useRef(null);

  // Sync display when value changes externally
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setIsOpen(false);
        // If query doesn't match a known institution, clear it
        const match = NIGERIAN_INSTITUTIONS.find(
          (i) => i.name.toLowerCase() === query.toLowerCase()
        );
        if (!match) {
          setQuery(value || "");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [query, value]);

  const getFiltered = () => {
    const q = query.trim().toLowerCase();
    const base =
      activeGroup === "all"
        ? NIGERIAN_INSTITUTIONS
        : getInstitutionsByType(activeGroup);

    if (!q) return base;
    return base.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        i.city.toLowerCase().includes(q)
    );
  };

  const filtered = getFiltered();

  // Group results by type for display
  const grouped = INSTITUTION_TYPES.reduce((acc, type) => {
    const items = filtered.filter((i) => i.type === type);
    if (items.length) acc[type] = items;
    return acc;
  }, {});

  const handleSelect = (institution) => {
    setQuery(institution.name);
    onChange(institution.name);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setIsOpen(true);
    if (!e.target.value) onChange("");
  };

  const handleClear = () => {
    setQuery("");
    onChange("");
    setIsOpen(false);
  };

  return (
    <div className="institution-select" ref={containerRef}>
      {/* Search input */}
      <div className={`institution-input-wrap ${isOpen ? "open" : ""} ${error ? "has-error" : ""}`}>
        <svg className="institution-search-icon" viewBox="0 0 16 16" fill="none">
          <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          className="institution-input"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {query && (
          <button type="button" className="institution-clear-btn" onClick={handleClear}>
            ✕
          </button>
        )}
        <svg
          className={`institution-chevron ${isOpen ? "flipped" : ""}`}
          viewBox="0 0 16 16"
          fill="none"
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="institution-dropdown">
          {/* Filter tabs */}
          <div className="institution-tabs">
            <button
              type="button"
              className={`institution-tab ${activeGroup === "all" ? "active" : ""}`}
              onClick={() => setActiveGroup("all")}
            >
              All
            </button>
            {INSTITUTION_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className={`institution-tab ${activeGroup === type ? "active" : ""}`}
                onClick={() => setActiveGroup(type)}
              >
                {type.replace("Federal ", "Fed. ").replace("State ", "St. ").replace(" University", " Uni").replace("College of Education", "Col. of Edu.")}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="institution-results">
            {Object.keys(grouped).length === 0 ? (
              <div className="institution-empty">
                <p>No institution found for "<strong>{query}</strong>"</p>
                <p className="institution-empty-sub">You can type your institution name manually</p>
                <button
                  type="button"
                  className="institution-use-typed"
                  onClick={() => { onChange(query); setIsOpen(false); }}
                >
                  Use "{query}"
                </button>
              </div>
            ) : (
              Object.entries(grouped).map(([type, items]) => (
                <div key={type} className="institution-group">
                  <div className="institution-group-label">{type}</div>
                  {items.map((inst) => (
                    <button
                      key={inst.name}
                      type="button"
                      className={`institution-option ${value === inst.name ? "selected" : ""}`}
                      onClick={() => handleSelect(inst)}
                    >
                      <span className="institution-option-name">{inst.name}</span>
                      <span className="institution-option-city">{inst.city}</span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {error && <p className="institution-error">{error}</p>}
    </div>
  );
};

export default InstitutionSelect;
